import React, { useEffect, useState } from 'react';

import { bool } from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';

import { Modal, UserTypeDetails, Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import HomeSections from './Sections/HomeSections';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { currentUserSelector, fetchCurrentUser } from '../../ducks/user.duck';
import { hasUserType } from '../../util/genericHelpers';
import {
  updateProfile,
  updateProfileInProgress,
} from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { useConfiguration } from '../../context/configurationContext';
import css from './LandingPage.module.css';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { useIntl } from 'react-intl';
import { authenticationInProgress, selectIsAuthenticated } from '../../ducks/auth.duck';

export const LandingPageComponent = props => {
  const { onManageDisableScrolling, scrollingDisabled } = props;

  const dispatch = useDispatch();
  const config = useConfiguration();
  const intl = useIntl();

  const [showMissingInformationReminder, setShowMissingInformationReminder] = useState(false);

  const currentUser = useSelector(currentUserSelector);
  const updateInProgress = useSelector(updateProfileInProgress);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authInProgress = useSelector(authenticationInProgress);

  const isUserTypeMissing = !hasUserType(currentUser);

  const { userFields, userTypes = [] } = config.user;

  useEffect(() => {
    if (isUserTypeMissing && isAuthenticated) {
      setShowMissingInformationReminder(isUserTypeMissing);
    }
  }, [currentUser, showMissingInformationReminder]);

  const handleUpdateProfile = values => {
    const updatedValues = Object.entries(values).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key.startsWith('pub_') ? key.slice(4) : key]: value,
      }),
      {}
    );

    const finalValues = {
      publicData: updatedValues,
    };

    dispatch(updateProfile(finalValues)).then(() => {
      dispatch(fetchCurrentUser());
      setShowMissingInformationReminder(false);
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
    });
  };

  const title = intl.formatMessage({ id: 'LandingPage.seoTitle' }, { marketplaceName: config.marketplaceName });
  const description = intl.formatMessage({ id: 'LandingPage.seoDescription' });

  return (
    <Page
      className={css.root}
      title={title}
      description={description}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <HomeSections />
      </LayoutSingleColumn>
      {isUserTypeMissing && isAuthenticated ? (
        <Modal
          id="MissingInformationReminder"
          containerClassName={css.containerClassName}
          isOpen={showMissingInformationReminder}
          onClose={() => {
            setShowMissingInformationReminder(!showMissingInformationReminder);
          }}
          usePortal
          hideCloseButton
          onManageDisableScrolling={onManageDisableScrolling}
          closeButtonMessage={intl.formatMessage({ id: 'ListingClaimModal.later' })}
        >
          <UserTypeDetails
            className={css.rootClassName}
            onSubmit={handleUpdateProfile}
            inProgress={updateInProgress}
            userFields={userFields}
            userTypes={userTypes}
          />
        </Modal>
      ) : null}
    </Page>
  );
};

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
};

const mapStateToProps = state => {
  return { scrollingDisabled: isScrollingDisabled(state) };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const LandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(LandingPageComponent);

export default LandingPage;
